import type { HowItWorksProps } from '@/components/giveback/HowItWorks';
import type { InternalImpactProps } from '@/components/giveback/InternalImpact';
import type { JoinTheGivebackProps } from '@/components/giveback/JoinTheGiveback';
import type { ImpactSectionProps } from '@/components/giveback/OurCommunityImpact';
import type { OurImpact2024Props } from '@/components/giveback/OurImpact2024';
import type { PublicBenefitProps } from '@/components/giveback/PublicBenefit';
import type { WhatIsGivebackProps } from '@/components/giveback/WhatIsGiveback';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/giveback_2024/OG.png';
import impact_1 from '@/static/assets/giveback_2024/impact_1.svg';
import impact_1_mobile from '@/static/assets/giveback_2024/impact_1_mobile.svg';
import impact_2 from '@/static/assets/giveback_2024/impact_2.svg';
import impact_2_mobile from '@/static/assets/giveback_2024/impact_2_mobile.svg';
import impact_3 from '@/static/assets/giveback_2024/impact_3.svg';
import impact_3_mobile from '@/static/assets/giveback_2024/impact_3_mobile.svg';
import impact_4 from '@/static/assets/giveback_2024/impact_4.svg';
import impact_4_mobile from '@/static/assets/giveback_2024/impact_4_mobile.svg';
import impact_5 from '@/static/assets/giveback_2024/impact_5.svg';
import impact_5_mobile from '@/static/assets/giveback_2024/impact_5_mobile.svg';
import works_1 from '@/static/assets/giveback_2024/works_1.svg';
import works_1_mobile from '@/static/assets/giveback_2024/works_1_mobile.svg';
import works_2 from '@/static/assets/giveback_2024/works_2.svg';
import works_2_mobile from '@/static/assets/giveback_2024/works_2_mobile.svg';
import type { BasePageConfig } from '../../BasePageConfig';

export interface Config extends BasePageConfig {
  whatIsGiveback: WhatIsGivebackProps;
  howItWorks: HowItWorksProps;
  ourImpact2024: OurImpact2024Props;
  ourCommunityImpact: ImpactSectionProps;
  joinTheGiveback: JoinTheGivebackProps;
  publicBenefit: PublicBenefitProps;
  internalImpact: InternalImpactProps;
}

export const config: Config = {
  seo: {
    title: 'The Lemonade Giveback and Social Impact',
    metaTitle: 'The Lemonade Giveback and Social Impact',
    description:
      'Once a year, the Lemonade community of customers help donate unclaimed money to nonprofits they care about. Check out how it works.',
    ogImage: ogImage.src,
    uniqueCanonicalUrl: 'https://www.lemonade.com/giveback', // TODO: remove this when the page is translated
  },
  layout: { ...sharedConfig['en-US'].layout },
  whatIsGiveback: {
    title: 'What is the Lemonade Giveback?',
    p1: 'Giveback is at the heart of Lemonade. This core feature of our business model empowers customers to support the causes they care about through their Lemonade insurance policies. Since 2017, we’ve donated over $10M to help build new homes, provide access to clean water, improve education, support animal rights and more.',
    p2: 'Here’s how it works: When you sign up for a Lemonade renters, homeowners, car, or pet insurance policy, we take a flat fee to grow and support our company. The rest of your premiums go towards paying claims. Any leftover money goes directly to one of our Giveback partners – nonprofits handpicked by our customers.',
  },
  howItWorks: {
    title: 'How Giveback Works',
    steps: [
      {
        text: 'You get a Lemonade policy, and\n<b>select a nonprofit</b> you care about',
        image: works_1,
        mobileImage: works_1_mobile,
      },
      {
        text: 'Once a year, we tally up the \n<b>unclaimed money</b> left from you\n and others who chose your cause',
        image: works_2,
        mobileImage: works_2_mobile,
      },
    ],
  },
  ourImpact2024: {
    title: 'Our Impact in 2024',
    subtitle: '43 nonprofits will be supported this year via the Lemonade Giveback.',
    years: [
      { url: '/blog/time-to-giveback', year: '2017' },
      { url: '/blog/lemonade-giveback-2018/', year: '2018' },
      { url: '/giveback-2019', year: '2019' },
      { url: '/giveback-2020', year: '2020' },
      { url: '/giveback-2021', year: '2021' },
      { url: '/giveback-2022', year: '2022' },
      { url: '/giveback-2023', year: '2023' },
    ],
    charities: [
      {
        org: 'charity: water',
        title: '15,000 people',
        desc: 'provided clean, running water in Malawi.',
      },
      {
        org: 'Pencils Of Promise',
        title: '1 brand new school',
        desc: 'built for a high-need community in Ghana.',
      },
      {
        org: 'New Story',
        title: '100 vulnerable families',
        desc: 'provided with housing in Jalisco, Mexico.',
      },
      {
        org: 'SPCAI',
        title: '12,247 animals',
        desc: 'will be provided with life-saving care, rescue, and shelter.',
      },
      {
        org: 'Malala Fund',
        title: '180 girls',
        desc: 'granted secondary education in five high-need schools in rural Bangladesh.',
      },
      {
        org: 'UNICEF USA',
        title: '12,253 children',
        desc: 'in emergency settings provided with clean and safe water.',
      },
      {
        org: 'American Forests',
        title: '20,000 trees',
        desc: 'planted to restore American landscapes after wildfires.',
      },
      {
        org: 'Robin Hood',
        title: '520 low-income households',
        desc: 'given life saving food, housing, and health benefits.',
      },
      {
        org: '350.org',
        title: '250 climate activists',
        desc: 'from the Global South trained to scale clean energy.',
      },
      {
        org: 'The Ocean Agency',
        title: '20,000 conservationists',
        desc: 'funded to protect our oceans.',
      },
      {
        org: 'Coalition For Rainforest Nations',
        title: '7,233 tons of CO2e emissions',
        desc: 'averted to protect our rainforests.',
      },
      {
        org: 'The Trevor Project',
        title: '950 LGBTQ+ young people',
        desc: 'received free, secure 24/7 mental health counseling.',
      },
      {
        org: 'En Avant Toute(S) - EU',
        title: '70 support sessions',
        desc: 'given to victims of abuse in France.',
      },
      {
        org: 'Direct Relief',
        title: '6,649 patients',
        desc: 'will receive emergency medical care in disaster zones.',
      },
      {
        org: 'March For Our Lives',
        title: '111,769 pledges to vote',
        desc: 'secured before the 2024 presidential election.',
      },
      {
        org: 'Born This Way',
        title: '80 schools',
        desc: 'funded with training in teen Mental Health First Aid.',
      },
      {
        org: 'A21',
        title: 'A 24/7 hotline',
        desc: 'funded to rescue trafficking victims in Greece, Bulgaria, and South Africa',
      },
      {
        org: 'Crisis Text Line',
        title: '70 emergency responses',
        desc: 'provided for mental health crises across the U.S.',
      },
      {
        org: 'CARE',
        title: '28 families',
        desc: 'received emergency food baskets in disaster areas.',
      },
      {
        org: 'National Breast Cancer Foundation',
        title: '281 Breast Health Kits',
        desc: 'delivered to raise awareness of breast cancer.',
      },
      {
        org: 'Cure Childhood Cancer',
        title: "12 children's",
        desc: 'prescriptions will be covered during cancer treatment.',
      },
      {
        org: 'DKMS',
        title: '142 stem cell donors',
        desc: 'added to the German National Bone Marrow Donor Registry.',
      },
      {
        org: 'To Write Love On Her Arms',
        title: '96,000 students',
        desc: "sponsored to join TWLOHA's high school program for mental health resources.",
      },
      {
        org: 'The Objective Zero Foundation',
        title: '24,500 American veterans',
        desc: 'given access to PTSD training via the Objective Zero app.',
      },
      {
        org: 'The French League Against Cancer',
        title: '30 Cancer Patients',
        desc: 'treated with physiotherapy.',
      },
      {
        org: 'KIKA',
        title: '1 childhood cancer research trial',
        desc: 'funded in the Netherlands.',
      },
      {
        org: 'Hôpitaux De Paris Hôpitaux De France',
        title: '10 Medical Staff',
        desc: 'supported with a relaxation area in CHU.',
      },
      {
        org: 'Teach for America',
        title: '251,300 students',
        desc: 'sponsored in high-need communities across the U.S.',
      },
      {
        org: 'Foster Love',
        title: '333 foster youth',
        desc: 'will experience the magic of Disneyland for the very first time.',
      },
      {
        org: 'Habitat For Humanity',
        title: '4,300 families',
        desc: 'granted safe housing in underserved communities in the U.S.',
      },
      {
        org: 'Citymeals On Wheels',
        title: '2,400 nutritious meals',
        desc: 'sent to homebound and elderly people in NYC.',
      },
      {
        org: 'The Ali Forney Center',
        title: '400 homeless LGBTQ+ youth',
        desc: 'provided with emergency responses.',
      },
      {
        org: "Emma's Torch",
        title: '1000 refugee chefs',
        desc: 'provided with culinary equipment.',
      },
      {
        org: 'WIN',
        title: '100 homeless youth',
        desc: 'provided with STEM programming.',
      },
      {
        org: 'Feeding America',
        title: '20,592 meals',
        desc: 'distributed to local food banks throughout the U.S.',
      },
      {
        org: 'Best Buddies',
        title: '704 students with IDD',
        desc: 'will receive support, resources and improved education.',
      },
      {
        org: 'Clean Clothes Campaign',
        title: '1 Campaign',
        desc: 'funded to support workers in the garment supply chain.',
      },
      {
        org: 'The Humane Society',
        title: '51,000 meals',
        desc: 'to nourish animals in need.',
      },
      {
        org: 'Muddy Paws',
        title: '365 days',
        desc: 'of cleaning and sanitation supplies for the Muddy Paws adoption van.',
      },
      {
        org: 'Pro-Wildlife',
        title: '1 campaign',
        desc: 'supported to strengthen wildlife protection in Germany.',
      },
    ],
  },
  ourCommunityImpact: {
    title: 'Our Community’s Impact',
    subtitle:
      "To date, Lemonade has donated over $10M to causes chosen by our customers.\n Here are some of the ways we're making a difference.",
    impactItems: [
      {
        title: 'Health',
        impact: '138,204 vaccines',
        desc: 'delivered to those in urgent need.',
        image: impact_2,
        imageMobile: impact_2_mobile,
      },
      {
        title: 'Emergency Response',
        impact: '91,997 veterans',
        desc: 'supported with PTSD training.',
        image: impact_3,
        imageMobile: impact_3_mobile,
      },
      {
        title: 'Global Poverty',
        impact: '52,341 people',
        desc: 'given safe, affordable housing.',
        image: impact_4,
        imageMobile: impact_4_mobile,
      },
      {
        title: 'Animal Rescue',
        impact: '128,568 animals',
        desc: 'received life-saving care.',
        image: impact_5,
        imageMobile: impact_5_mobile,
      },
      {
        title: 'Climate Action',
        impact: '40,836 people',
        desc: ' given safe drinking water for life.',
        image: impact_1,
        imageMobile: impact_1_mobile,
      },
    ],
  },
  publicBenefit: {
    title: 'We are a Public Benefit Corporation',
    p1: "Lemonade is a Public Benefit Corporation and a certified B Corp, meaning we're legally committed to making a positive social impact. The B Corp certification is awarded to businesses that prove they balance profit with purpose, focusing on people and the planet. Every three years, we undergo a comprehensive assessment of our entire business.",
  },
  internalImpact: {
    title: 'Internal Impact',
    p1: 'Globally, our Makers are involved in diverse impact initiatives, focusing on education, volunteering, and employee mentorship. We collaborate with local nonprofits to support our communities and prioritize using local and sustainable suppliers for office events.',
  },
  joinTheGiveback: {
    title: 'Join Us',
    subtitle:
      'Tens of thousands of policyholders are already supporting causes they care about just by having a Lemonade insurance policy. As our community grows, your social impact can become even more powerful.',
    mainCtaLabel: 'Get insured and giveback',
  },
};
